import { useEffect, useState } from "react";
import { IMailDetail } from "../../models/ISupportMail";
import { convertMailToTicket } from "../../services/supportMail";
import { getAllDepartments } from "../../ticket-module/services/ticketService";

interface Department { id: string; name: string; description: string; }

const PRIORITIES = [
  { value: 1, label: "Düşük" },
  { value: 2, label: "Orta" },
  { value: 3, label: "Yüksek" },
  { value: 4, label: "Kritik" },
];

interface Props {
  mail: IMailDetail;
  onClose: () => void;
  onConverted: (ticket: { ticketId: string; ticketNo: string; title: string }) => void;
}

export default function ConvertToTicketModal({ mail, onClose, onConverted }: Props) {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [departmentId, setDepartmentId] = useState("");
  const [priority, setPriority] = useState(4);
  const [title, setTitle] = useState("");
  const [addSenderToAllowList, setAddSenderToAllowList] = useState(false);
  const [includeWholeThread, setIncludeWholeThread] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getAllDepartments()
      .then((res) => {
        const list: Department[] = res.data ?? [];
        setDepartments(list);
        const destek = list.find((d) => d.name?.toLocaleLowerCase("tr").includes("destek"));
        if (destek) setDepartmentId(destek.id);
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await convertMailToTicket({
        messageId: mail.id,
        ticketDepartmentId: departmentId || undefined,
        priority,
        title: title.trim() || null,
        addSenderToAllowList,
        includeWholeThread,
      });
      onConverted(res.data.ticket);
      onClose();
    } catch (err: any) {
      const desc = err?.response?.data?.[0]?.description;
      setError(desc ?? "Ticket'a çevrilemedi. Lütfen tekrar deneyin.");
    } finally {
      setSaving(false);
    }
  };

  const inp: React.CSSProperties = {
    width: "100%", padding: "10px 14px", boxSizing: "border-box",
    border: "1.5px solid #e5e7eb", borderRadius: 10,
    fontSize: "0.88rem", outline: "none", color: "#111827",
  };
  const selectStyle: React.CSSProperties = { ...inp, background: "#fafafa", cursor: "pointer" };

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 10001, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 12px 48px rgba(0,0,0,0.22)" }}
      >
        {/* Header */}
        <div style={{
          padding: "20px 24px 18px", background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <i className="bi bi-arrow-repeat text-white" style={{ fontSize: "1.1rem" }}></i>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 800, fontSize: "1rem", color: "#fff" }}>Ticket'a Çevir</div>
              <div style={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.75)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{mail.subject}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", fontSize: "1.1rem", flexShrink: 0 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: 24 }}>
          {error && (
            <div style={{ marginBottom: 16, padding: "9px 14px", borderRadius: 10, background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", fontSize: "0.83rem", display: "flex", alignItems: "center", gap: 8 }}>
              <i className="bi bi-exclamation-circle-fill"></i>{error}
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "0.82rem", color: "#374151", marginBottom: 6 }}>Departman</label>
            <select value={departmentId} onChange={(e) => setDepartmentId(e.target.value)} style={selectStyle}>
              <option value="">— Varsayılan (Destek) —</option>
              {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "0.82rem", color: "#374151", marginBottom: 6 }}>Öncelik</label>
            <select value={priority} onChange={(e) => setPriority(Number(e.target.value))} style={selectStyle}>
              {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={{ display: "block", fontWeight: 600, fontSize: "0.82rem", color: "#374151", marginBottom: 6 }}>Başlık</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={mail.subject || "Mailin konusu kullanılacak"} maxLength={200} style={inp} />
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, background: "#f8faff", border: "1px solid #e0e7ff", cursor: "pointer", marginBottom: 10 }}>
            <input type="checkbox" checked={includeWholeThread} onChange={(e) => setIncludeWholeThread(e.target.checked)} style={{ width: 17, height: 17, accentColor: "#6366f1", cursor: "pointer" }} />
            <span style={{ fontSize: "0.84rem", color: "#374151" }}>Konuşmadaki tüm gelen mailleri ticket mesajı olarak ekle</span>
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 10, background: "#fffbeb", border: "1px solid #fde68a", cursor: "pointer", marginBottom: 20 }}>
            <input type="checkbox" checked={addSenderToAllowList} onChange={(e) => setAddSenderToAllowList(e.target.checked)} style={{ width: 17, height: 17, accentColor: "#d97706", cursor: "pointer" }} />
            <span style={{ fontSize: "0.84rem", color: "#92400e" }}>Göndereni beyaz listeye ekle (bundan sonraki mailleri filtrelenmesin)</span>
          </label>

          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <button onClick={onClose} disabled={saving} style={{ padding: "10px 20px", borderRadius: 10, border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", fontWeight: 600, fontSize: "0.86rem", cursor: "pointer" }}>
              İptal
            </button>
            <button onClick={handleSubmit} disabled={saving} style={{
              padding: "10px 24px", borderRadius: 10, border: "none",
              background: saving ? "#e5e7eb" : "linear-gradient(135deg, #7c3aed, #6d28d9)",
              color: saving ? "#9ca3af" : "#fff", fontWeight: 700, fontSize: "0.86rem",
              cursor: saving ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: 8,
            }}>
              {saving ? <><span className="spinner-border spinner-border-sm" role="status" /> Çevriliyor...</> : <><i className="bi bi-check2-circle"></i> Ticket'a Çevir</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
